// src/ui/debug/DebugPanel.tsx
//
// ⚠️ 本组件仅供 playtest / QA 使用，正式路径不可见。
//
// 双门控：
//   1. `import.meta.env.DEV` 为 true（生产构建静态替换为 false）
//   2. URL 含 `?debug=1`
// 两个门任一关闭 → 组件 return null（不进 DOM），绝不靠 CSS display:none 兜底。
//
// 控件语义：
//   - timeScale 切换：1 / 0.1 / 0.01（合法值与 src/core/debugParams.ts 一致）
//   - seed 输入框：任意有限整数；非法值在 debugParams.ts 内被丢弃为 null
//   - 清空：移除 debug / timeScale / seed 三个键，保留其他参数（如 ?tripMs=）
//
// 三个写 URL 的动作都以 location.reload() 收尾——store / rng 在挂载时一次性读取，
// 改 URL 不重读等于改了个寂寞。playtest 时按一下 reload 是可以接受的代价。
import { useState } from 'react';
import {
  isDebugMode,
  getTimeScale,
  getFixedSeed,
} from '../../core/debugParams';
import './DebugPanel.css';

/** 仅被本组件删除的 URL 键（debug + timeScale + seed）；其他查询参数全保留 */
const DEBUG_KEYS = ['debug', 'timeScale', 'seed'] as const;

type TimeScaleChoice = 1 | 0.1 | 0.01;
const TIME_SCALE_CHOICES: readonly TimeScaleChoice[] = [1, 0.1, 0.01];

/**
 * 把 `next` 合并进当前 URL 的 query string（保留非调试键），然后 reload。
 * `next` 中的 null 值会删除对应键；`undefined` 表示不动；其他值会 set。
 */
function applyUrlAndReload(next: {
  debug?: boolean;
  timeScale?: TimeScaleChoice | null;
  seed?: number | null;
}): void {
  const params = new URLSearchParams(window.location.search);

  // 1) 先清掉三个调试键（避免遗留旧值）
  for (const k of DEBUG_KEYS) {
    params.delete(k);
  }
  // 2) 再按 next 写入
  if (next.debug === true) params.set('debug', '1');
  if (typeof next.timeScale === 'number') params.set('timeScale', String(next.timeScale));
  if (typeof next.seed === 'number') params.set('seed', String(next.seed));

  // 3) 拼接新 search：URLSearchParams.toString() 已经过 encode
  const qs = params.toString();
  const newSearch = qs.length > 0 ? `?${qs}` : '';
  // 4) 用 reload-friendly 的方式更新 URL：pushState + reload
  window.history.replaceState({}, '', `${window.location.pathname}${newSearch}`);
  window.location.reload();
}

export function DebugPanel() {
  // 双门控：任一不满足 → 不渲染（return null，**不进 DOM**）
  if (!isDebugMode()) return null;

  const currentScale = getTimeScale();
  const currentSeed = getFixedSeed();

  // 受控输入框：本地 state 跟 input 走，提交时才写 URL
  const [seedInput, setSeedInput] = useState<string>(
    currentSeed !== null ? String(currentSeed) : '',
  );

  const handleTimeScaleClick = (scale: TimeScaleChoice): void => {
    applyUrlAndReload({ timeScale: scale });
  };

  const handleSeedApply = (): void => {
    const trimmed = seedInput.trim();
    if (trimmed === '') {
      // 空串视为"清掉 seed 但保留 debug"
      applyUrlAndReload({ seed: null });
      return;
    }
    const n = Number(trimmed);
    // 非有限数 / 非整数 → 不写 URL，让 debugParams 把它当 null 处理；
    // 仍然 reload 以便 URL 状态保持一致。
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
      applyUrlAndReload({ seed: null });
      return;
    }
    applyUrlAndReload({ seed: n });
  };

  const handleClear = (): void => {
    applyUrlAndReload({ debug: false, timeScale: null, seed: null });
  };

  return (
    <div
      className="debug-panel"
      role="region"
      aria-label="调试面板（仅开发与 playtest）"
      data-testid="debug-panel"
    >
      <div className="debug-panel__title">调试 · Debug</div>

      <div className="debug-panel__row">
        <span className="debug-panel__label">timeScale</span>
        <div className="debug-panel__seg" role="group" aria-label="timeScale 切换">
          {TIME_SCALE_CHOICES.map((s) => {
            const active = s === currentScale;
            return (
              <button
                key={s}
                type="button"
                className={`debug-panel__seg-btn${active ? ' is-active' : ''}`}
                aria-pressed={active}
                onClick={() => handleTimeScaleClick(s)}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="debug-panel__row">
        <label className="debug-panel__label" htmlFor="debug-seed-input">
          seed
        </label>
        <input
          id="debug-seed-input"
          className="debug-panel__input"
          type="number"
          inputMode="numeric"
          step={1}
          value={seedInput}
          onChange={(e) => setSeedInput(e.target.value)}
          placeholder={currentSeed === null ? '随机' : String(currentSeed)}
          aria-label="固定种子（整数）"
        />
        <button
          type="button"
          className="debug-panel__btn"
          onClick={handleSeedApply}
        >
          应用
        </button>
      </div>

      <button
        type="button"
        className="debug-panel__btn debug-panel__btn--danger"
        onClick={handleClear}
      >
        清空调试参数
      </button>
    </div>
  );
}
